<script setup lang="ts">
import { computed, onUnmounted, provide, ref, watch } from 'vue';
import { RouterLink, RouterView, useRouter } from 'vue-router';
import { api } from '@/lib/api';
import { XMarkIcon } from '@heroicons/vue/20/solid';
import {
  SidebarLayout,
  Sidebar,
  SidebarHeader,
  SidebarBody,
  SidebarFooter,
  SidebarSection,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSpacer,
  Navbar,
  NavbarItem,
  Button,
  ConfirmDialog,
} from '@/ui';
import { useAuthStore } from '@/stores/auth';
import { useUiModeStore } from '@/stores/uiMode';
import {
  HomeIcon,
  UsersIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserPlusIcon,
  IdentificationIcon,
  DocumentTextIcon,
  TagIcon,
  Cog6ToothIcon,
  BookOpenIcon,
  ChevronDoubleLeftIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ArrowPathIcon,
  AdjustmentsHorizontalIcon,
  KeyIcon,
} from '@heroicons/vue/24/outline';

const auth = useAuthStore();
const uiMode = useUiModeStore();
const router = useRouter();

// Switching mode also lands the user on the target mode's home, so they aren't
// left on a route that belongs to the other mode with mismatched navigation.
function switchMode(next: 'simple' | 'advanced'): void {
  uiMode.setMode(next);
  void router.push(next === 'simple' ? '/simple' : '/');
}

// Collapsible desktop sidebar. Persisted so the operator's choice sticks across
// loads. Provided to SidebarLabel/SidebarHeading so they hide in the icon rail.
const navCollapsed = ref(localStorage.getItem('af-nav-collapsed') === '1');
provide('sidebarCollapsed', navCollapsed);
function toggleNav(): void {
  navCollapsed.value = !navCollapsed.value;
  localStorage.setItem('af-nav-collapsed', navCollapsed.value ? '1' : '0');
}

// Spec §18.1 admin update banner. Reads the last update-check result once the
// user is signed in and is an Admin; failures are swallowed (never blocks).
interface UpdateStatus {
  currentVersion: string | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  htmlUrl: string | null;
  checkedAt: string | null;
}
const update = ref<UpdateStatus | null>(null);
const updateDismissed = ref(false);

// Spec §10.1 marker-slot banner. Served from the resolution the backend settled
// at startup, so this is an in-memory read rather than a directory scan.
interface MarkerSlotStatus {
  slot: string;
  configured: string;
  pinned: string | null;
  source: 'pinned' | 'configured';
  state: 'pinned' | 'configured' | 'contested' | 'ambiguous';
  mismatch: boolean;
  ambiguousSlots?: Array<{ slot: string; count: number }>;
  contested?: {
    reason: 'configured_explicitly' | 'cache_incomplete' | 'multiple_slots';
    inferred: string;
    inferredCount: number;
  };
  writesBlocked: boolean;
}
const markerSlot = ref<MarkerSlotStatus | null>(null);

// Spec §7.0. An AD-created account is keyed on its objectGUID until the
// reconciler joins it to its Entra object; anything that could not be joined,
// or was withheld, sits in a queue. A queue nobody looks at is the failure mode
// this whole feature exists to prevent, so it gets a banner.
interface AdReconcileSummary {
  adEnabled: boolean;
  actionable: number;
  ambiguous: number;
  unmatched: number;
  withheld: number;
  lastRunStatus: string | null;
}
const adReconcile = ref<AdReconcileSummary | null>(null);
const adReconcileNeedsAttention = computed(() => {
  const r = adReconcile.value;
  if (!r) return false;
  return (
    r.actionable > 0 ||
    r.ambiguous > 0 ||
    r.unmatched > 0 ||
    r.withheld > 0 ||
    (r.lastRunStatus !== null && r.lastRunStatus !== 'success')
  );
});

watch(
  () => auth.signedIn,
  async (signedIn) => {
    if (!signedIn || !auth.hasRole('Admin')) return;
    if (!adReconcile.value) {
      try {
        const res = await api.get<{
          plan: {
            adEnabled: boolean;
            actionable: number;
            counts: { ambiguous: number; unmatched: number };
            lastRun: { status: string | null };
          };
          withheldBindings: unknown[];
        }>('/diagnostics/ad-reconcile');
        adReconcile.value = {
          adEnabled: res.plan.adEnabled,
          actionable: res.plan.actionable,
          ambiguous: res.plan.counts.ambiguous,
          unmatched: res.plan.counts.unmatched,
          withheld: res.withheldBindings.length,
          lastRunStatus: res.plan.lastRun.status,
        };
      } catch {
        /* the banner is a report, not a gate */
      }
    }
    if (!update.value) {
      try {
        update.value = await api.get<UpdateStatus>('/update/status');
      } catch {
        /* never block the app on the update check */
      }
    }
    if (!markerSlot.value) {
      try {
        markerSlot.value = (
          await api.get<{ provenance: MarkerSlotStatus | null }>('/diagnostics/marker-slot')
        ).provenance;
      } catch {
        /* the banner is a report, not a gate */
      }
    }
  },
  { immediate: true },
);

// Free-tier Azure SQL auto-pauses when idle and takes ~30-60s to resume on the
// first request. The backend runs migrations in the background on startup and
// flips /health/db to ready once SQL is up and the schema is present. Gate the
// signed-in shell on that so the first SQL-backed view shows a clear "warming
// up" spinner instead of erroring or hanging. Polls until ready, then proceeds
// regardless after a cap so a genuinely unreachable DB never traps the user
// (individual views surface their own errors in that case).
const dbReady = ref(false);
let dbPollTimer: ReturnType<typeof setTimeout> | undefined;

async function waitForDb(): Promise<void> {
  const startedAt = Date.now();
  const maxWaitMs = 120_000;
  const check = async (): Promise<void> => {
    let ready = false;
    try {
      const res = await fetch('/health/db', { cache: 'no-store' });
      if (res.ok) ready = ((await res.json()) as { ready: boolean }).ready;
    } catch {
      /* cold start / transient network – keep polling */
    }
    if (ready || Date.now() - startedAt > maxWaitMs) {
      dbReady.value = true;
      return;
    }
    dbPollTimer = setTimeout(() => void check(), 2000);
  };
  await check();
}

watch(
  () => auth.signedIn,
  (signedIn) => {
    if (signedIn && !dbReady.value) void waitForDb();
  },
  { immediate: true },
);

onUnmounted(() => {
  if (dbPollTimer) clearTimeout(dbPollTimer);
});
</script>

<template>
  <div v-if="!auth.signedIn" class="min-h-svh bg-zinc-100 dark:bg-zinc-950 flex items-center justify-center p-6">
    <div class="w-full max-w-md rounded-lg bg-white p-8 shadow-xs ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
      <h1 class="text-2xl font-semibold text-zinc-950 dark:text-white">Account Factory</h1>
      <p class="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
        Sign in with your work account to continue.
      </p>
      <p v-if="auth.initError" class="mt-4 text-sm text-red-600 dark:text-red-500">
        Startup error: {{ auth.initError }}
      </p>
      <div class="mt-6">
        <Button color="dark/zinc" @click="auth.signIn">
          <ArrowRightOnRectangleIcon class="size-4" /> Sign in
        </Button>
      </div>
    </div>
  </div>

  <div v-else-if="!dbReady" class="min-h-svh bg-zinc-100 dark:bg-zinc-950 flex items-center justify-center p-6">
    <div class="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xs ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
      <ArrowPathIcon class="mx-auto size-8 animate-spin text-zinc-400 dark:text-zinc-500" />
      <h1 class="mt-4 text-lg font-semibold text-zinc-950 dark:text-white">Waking up the database</h1>
      <p class="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
        The free Azure SQL tier pauses when idle, so the first sign-in after a quiet
        spell takes around 30 to 60 seconds while it resumes. Upgrade the SQL tier to
        avoid this delay.
      </p>
    </div>
  </div>

  <SidebarLayout v-else width="wide" :collapsed="navCollapsed">
    <template #navbar>
      <Navbar>
        <span class="text-sm font-semibold text-zinc-950 dark:text-white truncate">Account Factory</span>
      </Navbar>
    </template>

    <template #sidebar>
      <Sidebar>
        <SidebarHeader>
          <div class="flex items-center gap-2 px-2" :class="navCollapsed ? 'justify-center' : 'justify-between'">
            <div v-show="!navCollapsed" class="flex min-w-0 items-center gap-2">
              <div class="flex size-8 shrink-0 items-center justify-center rounded-md bg-zinc-900 dark:bg-white">
                <span class="text-sm font-semibold text-white dark:text-zinc-900">AF</span>
              </div>
              <SidebarLabel class="text-sm font-semibold text-zinc-950 dark:text-white">Account Factory</SidebarLabel>
            </div>
            <button
              type="button"
              class="hidden shrink-0 rounded-md p-1.5 text-zinc-500 hover:bg-zinc-950/5 hover:text-zinc-950 lg:flex dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white"
              :title="navCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
              @click="toggleNav"
            >
              <ChevronDoubleLeftIcon :class="['size-5 transition-transform', navCollapsed ? 'rotate-180' : '']" />
            </button>
          </div>
        </SidebarHeader>

        <SidebarBody>
          <!-- Simple mode: a short, verb-first nav for the account operator. -->
          <template v-if="uiMode.isSimple">
            <SidebarSection>
              <SidebarItem href="/simple">
                <HomeIcon /> <SidebarLabel>Home</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/simple/add">
                <UserPlusIcon /> <SidebarLabel>Add a person</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/simple/people">
                <UsersIcon /> <SidebarLabel>People</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/my-team">
                <UserGroupIcon /> <SidebarLabel>My team</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSpacer />

            <SidebarSection>
              <SidebarItem @click="switchMode('advanced')">
                <AdjustmentsHorizontalIcon /> <SidebarLabel>Full view</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/simple/help">
                <BookOpenIcon /> <SidebarLabel>Help</SidebarLabel>
              </SidebarItem>
            </SidebarSection>
          </template>

          <!-- Advanced mode: the full app. -->
          <template v-else>
            <SidebarSection>
              <SidebarItem href="/">
                <HomeIcon /> <SidebarLabel>Home</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/my-team">
                <UserCircleIcon /> <SidebarLabel>My team</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSection v-if="auth.hasRole('Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk')">
              <SidebarHeading>People</SidebarHeading>
              <SidebarItem v-if="auth.hasRole('Admin', 'ProfileAdmin', 'Auditor', 'HelpDesk')" href="/users">
                <UsersIcon /> <SidebarLabel>Users</SidebarLabel>
              </SidebarItem>
              <SidebarItem v-if="auth.hasRole('Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk')" href="/groups">
                <UserGroupIcon /> <SidebarLabel>Groups</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSection v-if="auth.hasRole('Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk')">
              <SidebarHeading>Configuration</SidebarHeading>
              <!-- Profiles is where the deployment is configured, not where
                   accounts are operated. A scoped Profile Admin administers
                   users under a profile without authoring one, and the server
                   now scopes what they would see anyway (§5.4) - so offering it
                   to them is a page of read-only settings and one profile. -->
              <SidebarItem
                v-if="auth.hasRole('Admin', 'ProfileManager', 'Auditor', 'HelpDesk') || auth.globalProfileAdmin"
                href="/profiles"
              >
                <IdentificationIcon /> <SidebarLabel>Profiles</SidebarLabel>
              </SidebarItem>
              <SidebarItem v-if="auth.hasRole('Admin', 'ProfileAdmin', 'Auditor', 'HelpDesk')" href="/audit">
                <DocumentTextIcon /> <SidebarLabel>Audit</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSpacer />

            <SidebarSection v-if="auth.hasRole('Admin')">
              <SidebarHeading>Administration</SidebarHeading>
              <SidebarItem href="/admin/config">
                <Cog6ToothIcon /> <SidebarLabel>Settings</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/admin/tag-rules">
                <TagIcon /> <SidebarLabel>Tag rules</SidebarLabel>
              </SidebarItem>
            </SidebarSection>

            <SidebarSection>
              <!-- An API key is a standing non-interactive credential carrying
                   its owner's authority (§23.3). The route still admits a
                   scoped Profile Admin - a key they mint stays fenced to their
                   own cohort - but it is not something to put in front of the
                   everyday account operator, who has no use for one. -->
              <SidebarItem
                v-if="auth.hasRole('Admin', 'ProfileManager', 'Auditor', 'HelpDesk') || auth.globalProfileAdmin"
                href="/api-keys"
              >
                <KeyIcon /> <SidebarLabel>API keys</SidebarLabel>
              </SidebarItem>
              <SidebarItem v-if="uiMode.canUseSimple" @click="switchMode('simple')">
                <AdjustmentsHorizontalIcon /> <SidebarLabel>Simple view</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/docs">
                <BookOpenIcon /> <SidebarLabel>Docs</SidebarLabel>
              </SidebarItem>
            </SidebarSection>
          </template>
        </SidebarBody>

        <SidebarFooter>
          <div class="flex items-center gap-2 px-1" :class="navCollapsed ? 'justify-center' : 'justify-between'">
            <div v-show="!navCollapsed" class="min-w-0">
              <p class="truncate text-sm font-medium text-zinc-950 dark:text-white">{{ auth.displayName ?? auth.upn }}</p>
              <p class="truncate text-xs text-zinc-500 dark:text-zinc-400">{{ auth.upn }}</p>
            </div>
            <button
              type="button"
              class="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-950/5 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white"
              title="Sign out"
              @click="auth.signOut"
            >
              <ArrowLeftOnRectangleIcon class="size-5" />
            </button>
          </div>
        </SidebarFooter>
      </Sidebar>
    </template>

    <div
      v-if="update?.updateAvailable && !updateDismissed"
      class="mb-4 flex items-start justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300"
    >
      <p>
        A newer Account Factory release is available
        (<span class="font-mono">{{ update.latestVersion }}</span>, this instance runs
        <span class="font-mono">{{ update.currentVersion }}</span>).
        <a v-if="update.htmlUrl" :href="update.htmlUrl" target="_blank" rel="noopener" class="underline">Release notes</a>.
        Updates are applied through your own deployment pipeline.
      </p>
      <button
        type="button"
        class="shrink-0 rounded-md p-1 text-blue-700 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/30"
        aria-label="Dismiss"
        @click="updateDismissed = true"
      >
        <XMarkIcon class="size-4" />
      </button>
    </div>

    <!--
      Spec §10.1. Deliberately not dismissible: the app is declining to act on a
      value an operator set, and a default moving silently is what made this
      necessary in the first place.
    -->
    <div
      v-if="markerSlot?.ambiguousSlots"
      class="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400"
    >
      <p>
        The <span class="font-mono">managedBy</span> marker is present in more than one extension
        attribute
        (<span class="font-mono">{{ markerSlot.ambiguousSlots.map((s) => `${s.slot} (${s.count})`).join(', ') }}</span>),
        so Account Factory cannot tell which slot it owns. It is reading
        <span class="font-mono">{{ markerSlot.slot }}</span> meanwhile, accounts stamped under the
        other slots will not read as managed, and <strong>no new account can be stamped</strong>
        until this is resolved.
        <RouterLink to="/admin/config" class="font-medium underline">
          Re-stamp them onto one slot under Admin -&gt; Diagnostics
        </RouterLink>
        &ndash; the re-stamp has a dry run that writes nothing.
      </p>
    </div>

    <!--
      The state the app refuses to settle itself: evidence from the directory
      against an instruction someone gave. Red rather than amber because nothing
      can be stamped until a person answers, and it names both answers.
    -->
    <div
      v-else-if="markerSlot?.state === 'contested'"
      class="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400"
    >
      <p v-if="markerSlot.contested?.reason === 'cache_incomplete'">
        The <span class="font-mono">managedBy</span> marker is on
        {{ markerSlot.contested.inferredCount }} account<span v-if="markerSlot.contested.inferredCount !== 1">s</span>
        in <span class="font-mono">{{ markerSlot.contested.inferred }}</span>, but
        <span class="font-mono">EXT_ATTR_MANAGED_BY</span> says
        <span class="font-mono">{{ markerSlot.configured }}</span> and the directory cache is not
        complete enough to rule out other slots. Refresh the directory, then choose.
      </p>
      <p v-else>
        <span class="font-mono">EXT_ATTR_MANAGED_BY</span> is set to
        <span class="font-mono">{{ markerSlot.configured }}</span>, but the
        <span class="font-mono">managedBy</span> marker is on
        {{ markerSlot.contested?.inferredCount }} account<span v-if="markerSlot.contested?.inferredCount !== 1">s</span>
        in <span class="font-mono">{{ markerSlot.contested?.inferred }}</span>. Account Factory will
        not overrule a slot you set, so it has stamped nothing new.
      </p>
      <p class="mt-2">
        Reads use <span class="font-mono">{{ markerSlot.slot }}</span>, so nothing is hidden, but
        <strong>no account can be created or taken over</strong> until you choose:
        <RouterLink to="/admin/config" class="font-medium underline">
          under Admin -&gt; Diagnostics
        </RouterLink>
        keep <span class="font-mono">{{ markerSlot.contested?.inferred }}</span>, or re-stamp those
        accounts onto <span class="font-mono">{{ markerSlot.configured }}</span> (dry run first
        &ndash; it writes nothing).
      </p>
    </div>

    <div
      v-else-if="markerSlot?.mismatch"
      class="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
    >
      <p>
        Account Factory is using <span class="font-mono">{{ markerSlot.slot }}</span> for its
        <span class="font-mono">managedBy</span> marker, because that is where this deployment's
        accounts are actually stamped.
        <span class="font-mono">EXT_ATTR_MANAGED_BY</span> is set to
        <span class="font-mono">{{ markerSlot.configured }}</span> and is being ignored, so no
        accounts have been lost. To move the marker to
        <span class="font-mono">{{ markerSlot.configured }}</span>,
        <RouterLink to="/admin/config" class="font-medium underline">
          run the re-stamp under Admin -&gt; Diagnostics
        </RouterLink>
        (dry run first &ndash; it writes nothing). To clear this notice instead, set
        <span class="font-mono">EXT_ATTR_MANAGED_BY</span> back to
        <span class="font-mono">{{ markerSlot.slot }}</span>.
      </p>
    </div>

    <!--
      Spec §7.0. Not dismissible: an AD account that is not joined to its Entra
      object reads as managed but carries no binding the leaver can find, so it
      fails at the end of the flow rather than the start.
    -->
    <div
      v-if="adReconcile?.adEnabled && adReconcileNeedsAttention"
      class="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
    >
      <p>
        <template v-if="adReconcile.actionable > 0">
          {{ adReconcile.actionable }} AD-created account<span v-if="adReconcile.actionable !== 1">s are</span><span v-else=""> is</span>
          waiting to be joined to their Entra object. Until then they show no profile and the leaver
          cannot complete for them.
        </template>
        <template v-if="adReconcile.ambiguous + adReconcile.unmatched + adReconcile.withheld > 0">
          {{ adReconcile.ambiguous + adReconcile.unmatched + adReconcile.withheld }} need a decision
          that only a person can make.
        </template>
        <RouterLink to="/admin/config" class="font-medium underline">
          Resolve under Admin -&gt; Diagnostics
        </RouterLink>.
      </p>
    </div>

    <p v-if="auth.initError" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
      Startup error: {{ auth.initError }}
    </p>
    <RouterView v-else v-slot="{ Component }">
      <KeepAlive :include="['UsersView', 'GroupsView']">
        <component :is="Component" />
      </KeepAlive>
    </RouterView>
  </SidebarLayout>

  <ConfirmDialog />
</template>
